import React from 'react'
import { motion } from 'framer-motion'

const services = [
  {
    title: "Vectorization",
    description: "We take your any raster image and recreate them into clean image ready for vector art.",
    features: [
      "Handmade Vector, no auto vector/tracing",
      "Redrawing",
      "All Major Formats",
      "Outstanding Quality with Fastest Turnaround Time",
      "Virtual Samples"
    ],
    image: "/Home/vector.jpg",
    gradient: "from-orange-500 to-amber-500",
    solid: "bg-orange-500",
    tint: "bg-orange-50",
    glow: "bg-orange-300/50",
    frame: "border-orange-400/70",
    block: "from-orange-400 to-amber-300",
  },
  {
    title: "Digitization",
    description: "We customize files for Embroidery work.",
    features: [
      "Affordable Digitizing without compromising the Quality.",
      "Fastest Turnaround time by 4 to 8 Hours",
      "Provide all Major Format",
      "Expert in Wilcom, Melco and Pulse",
      "Free Minor Editing, Revision and Resizing"
    ],
    image: "/Home/before11.jpg",
    gradient: "from-red-500 to-rose-500",
    solid: "bg-red-500",
    tint: "bg-red-50",
    glow: "bg-red-300/50",
    frame: "border-red-400/70",
    block: "from-red-400 to-rose-300",
  },
  {
    title: "Patches",
    description: "Patches are a great tool for promoting a company's brand and presence. Uniforms can display a vibrant company logo or an employee name. Our patches are fully customizable with multiple options to suit your organization's needs.",
    features: [
      "Custom Design Patches",
      "Company Branding",
      "Employee Name Patches",
      "Multiple Material Options",
      "Professional Quality"
    ],
    image: "/portfolio-snapshot/patches/p4.jpg",
    gradient: "from-amber-500 to-orange-500",
    solid: "bg-amber-500",
    tint: "bg-amber-50",
    glow: "bg-amber-300/50",
    frame: "border-amber-400/70",
    block: "from-amber-400 to-orange-300",
  }
]

const reasons = [
  { title: "Expert Quality", description: "Professional results with attention to every detail", bar: "bg-orange-500" },
  { title: "Fast Turnaround", description: "Quick delivery without compromising quality", bar: "bg-amber-500" },
  { title: "Affordable Pricing", description: "Competitive rates for professional services", bar: "bg-red-500" }
]

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, delay },
})

const ServicesPage = () => {
  return (
    <div className="pt-20 min-h-screen bg-white text-slate-900 antialiased">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(55rem 26rem at 90% -10%, rgba(249,115,22,.45), transparent 60%), radial-gradient(40rem 22rem at -5% 110%, rgba(244,63,94,.25), transparent 60%)'
          }}
        />
        <svg
          aria-hidden="true"
          className="absolute bottom-8 left-0 w-full h-28 text-[#F97316]/50"
          viewBox="0 0 1200 112"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 70 C 200 10, 400 120, 600 62 S 1000 10, 1200 70"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeDasharray="10 9"
            strokeLinecap="round"
          />
        </svg>
        <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-40">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-6xl md:text-8xl font-extrabold tracking-tight leading-[0.98]"
          >
            Our{' '}
            <span className="bg-gradient-to-r from-orange-400 via-red-400 to-amber-400 bg-clip-text text-transparent">
              Services
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-lg md:text-2xl text-slate-300 max-w-2xl leading-relaxed"
          >
            Professional vector art, embroidery digitization, and custom patch services
          </motion.p>
        </div>
      </section>

      {/* Services List */}
      <section className="relative overflow-hidden">
        {services.map((service, index) => (
          <div
            key={service.title}
            className={`relative overflow-hidden py-20 md:py-28 ${index % 2 === 1 ? service.tint : 'bg-white'}`}
          >
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute h-96 w-96 rounded-full ${service.glow} blur-3xl ${
                index % 2 === 1 ? '-top-32 -left-32' : '-bottom-32 -right-32'
              }`}
            />
            <motion.div
              {...reveal()}
              className={`relative max-w-6xl mx-auto px-6 grid items-center gap-14 lg:gap-20 lg:grid-cols-2 ${
                index % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              {/* Service Image */}
              <div className="relative w-full">
                <div
                  aria-hidden="true"
                  className={`absolute -bottom-5 ${index % 2 === 1 ? '-left-5' : '-right-5'} h-2/3 w-2/3 rounded-[1.75rem] bg-gradient-to-br ${service.block}`}
                />
                <div
                  aria-hidden="true"
                  className={`absolute -top-4 ${index % 2 === 1 ? '-right-4' : '-left-4'} h-full w-full rounded-[1.75rem] border-2 border-dashed ${service.frame}`}
                />
                <div className="relative overflow-hidden rounded-xl shadow-2xl ring-4 ring-white">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>

              {/* Service Content */}
              <div className="w-full">
                <h3 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1] mb-5">
                  <span className={`bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}>
                    {service.title}
                  </span>
                </h3>
                <div className={`mb-7 h-1.5 w-20 rounded-full ${service.solid}`} />

                <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                  {service.description}
                </p>

                <ul className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm divide-y divide-dashed divide-slate-200">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-4 px-6 py-4 transition-colors hover:bg-slate-50"
                    >
                      <span className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${service.solid} text-xs text-white`}>
                        ✓
                      </span>
                      <span className="text-slate-700 font-medium leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        ))}
      </section>

      {/* Why Choose Our Services */}
      <section className="relative overflow-hidden bg-slate-900 py-24 md:py-32 text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-orange-500/30 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-rose-500/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            {...reveal()}
            className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-end"
          >
            <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Why Choose Our{' '}
              <span className="bg-gradient-to-r from-orange-400 via-red-400 to-amber-400 bg-clip-text text-transparent">
                Services
              </span>
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              We provide exceptional service with a focus on quality, speed, and customer satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {reasons.map((item, index) => (
              <motion.div
                key={item.title}
                {...reveal(index * 0.1)}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 pt-10 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/10"
              >
                <div className={`absolute left-0 top-0 h-1.5 w-full ${item.bar}`} />
                <h3 className="text-2xl font-bold tracking-tight mb-3">{item.title}</h3>
                <div className="mb-4 border-t border-dashed border-white/20" />
                <p className="text-slate-300 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-white px-6 py-24 md:py-32">
        <motion.div
          {...reveal()}
          className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-slate-900 px-8 py-16 md:py-24 text-center text-white"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-orange-500/40 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-orange-400/30" />
          <div className="relative">
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5">
              Ready to Get{' '}
              <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                Started
              </span>
              ?
            </h2>
            <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-xl mx-auto leading-relaxed">
              Let's discuss your project and bring your vision to life with our expert team.
            </p>
            <a
              href="/contact"
              className="inline-block rounded-xl bg-[#F97316] px-9 py-4 text-lg font-semibold text-white transition-colors hover:bg-[#EA580C] focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-400/50"
            >
              Contact Us Now
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  )
}

export default ServicesPage